import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import Reveal, { EASE } from "./Reveal";
import SectionHeading from "./SectionHeading";
import ArtFrame from "./ArtFrame";

const Books = ({ books }) => {
  const [active, setActive] = useState(null);
  const stories = books.filter((b) => b.status !== "coming_soon");

  return (
    <section id="books" data-testid="books-section" className="relative z-10 px-6 py-28 md:px-16 lg:px-24 lg:py-40">
      <SectionHeading
        title="BOOKS"
        eyebrow="Every story reveals another piece of the Cosmic Dreams Universe. Five parts — one connected saga."
      />
      <div className="grid grid-cols-2 gap-5 md:grid-cols-3 md:gap-8 xl:grid-cols-5">
        {stories.map((book, i) => (
          <Reveal key={book.id} delay={0.08 * i}>
            <div
              data-testid={`book-card-${book.id}`}
              className="group cursor-pointer"
              onClick={() => setActive(book)}
            >
              <ArtFrame
                image={book.image}
                hue={book.hue}
                label={`COVER — ${book.part}`}
                className="border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.7)] transition-all duration-700 group-hover:border-[#D4AF37]/40 group-hover:-translate-y-2"
              />
              <div className="mt-5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className={`${book.image ? "text-sm tracking-wider text-[#F5F5FA]" : "font-mono text-[9px] tracking-[0.3em] text-[#606070] uppercase"}`}>
                  {book.image ? book.title : "Cosmic Dreams Saga"}
                </span>
                <button
                  data-testid={`book-explore-${book.id}`}
                  className="shrink-0 font-mono text-[10px] tracking-[0.3em] text-[#A0A0B0] transition-colors duration-500 group-hover:text-[#D4AF37]"
                >
                  EXPLORE STORY →
                </button>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {createPortal(
        <AnimatePresence>
          {active && (
            <motion.div
              data-testid="book-modal"
              data-lenis-prevent
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="fixed inset-0 z-[110] flex items-center justify-center bg-[#030305]/90 p-6 backdrop-blur-md"
              onClick={() => setActive(null)}
            >
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 50, opacity: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="relative flex max-h-[85vh] w-full max-w-3xl flex-col gap-8 overflow-y-auto border border-white/10 bg-[#0A0A10]/90 p-8 md:flex-row md:p-12"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  data-testid="book-modal-close"
                  onClick={() => setActive(null)}
                  className="absolute right-4 top-4 text-[#A0A0B0] transition-colors hover:text-[#D4AF37]"
                  aria-label="Close"
                >
                  <X size={20} strokeWidth={1.25} />
                </button>
                <div className="w-40 shrink-0 md:w-48">
                  <ArtFrame image={active.image} hue={active.hue} label={`COVER — ${active.part}`} className="border border-white/10" />
                </div>
                <div>
                  <span className="font-mono text-[10px] tracking-[0.4em] text-[#D4AF37]">{active.part}</span>
                  <h3 className="mt-3 font-serif text-2xl md:text-4xl tracking-wide text-[#F5F5FA]">{active.title}</h3>
                  <p className="mt-6 font-tech text-[9px] uppercase text-[#606070]">About the book</p>
                  <p className="mt-3 max-w-md text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">
                    {active.description}
                  </p>
                  {active.characters && (
                    <>
                      <p className="mt-6 font-tech text-[9px] uppercase text-[#606070]">Main characters</p>
                      <p className="mt-2 text-sm tracking-wider text-[#F5F5FA]/90">{active.characters}</p>
                    </>
                  )}
                  <p className="mt-8 font-mono text-[9px] tracking-[0.3em] text-[#606070] uppercase">
                    Read / purchase links arrive with the official release
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
};

export default Books;
